// Objets du sac, définis dans l'administration (onglet Objets). Partagé par le serveur (combat, boutiques,
// quêtes), le jeu (sac, boutique, fiche des monstres) et l'administration.
import type { IconName } from './icons.ts';
import { strongestLight, type LightSource } from './lighting.ts';
import { isKeyOf } from './guards.ts';

/** Identifiant stable d'un objet (« potion », « super-capsule »…) : il sert de clé dans les sacs, coffres et quêtes. */
export type ItemId = string;

/** Utilité d'un objet. */
export type ItemEffect =
  /**
   * Objet de quête ou de collection : il ne s'utilise pas. `category` (« Lettres »…), libre et créée à la volée dans
   * l'administration, le range dans une sous-rubrique des objets de quête du sac.
   */
  | { type: 'none'; category?: string }
  /** Rend des PV à un monstre (en combat ou depuis l'équipe). */
  | { type: 'heal'; amount: number }
  /** Se lance sur un monstre sauvage pour le capturer ; `bonus` multiplie le taux de capture. */
  | { type: 'capture'; bonus: number }
  /** S'équipe depuis le sac à dos (sans s'user) et donne une capacité tant qu'il est équipé. */
  | EquipmentEffect
  /** S'utilise depuis le sac, hors combat, et s'use (voir FieldAction). */
  | FieldEffect
  /**
   * Attaque à apprendre (comme les CT dans Pokémon) : tant qu'il est dans le sac, les {monstres} dont un type est celui
   * de l'attaque peuvent l'apprendre (tous, pour une attaque de type normal), à volonté (l'objet ne s'use pas).
   */
  | { type: 'move'; moveId: number };

/**
 * Objet utilisable sur la carte. `repel` : aucune rencontre de {monstre} sauvage pendant `steps` pas ;
 * `escape` : retour au dernier point de soin (la dernière borne utilisée, sinon le départ de la carte).
 */
export type FieldEffect = { type: 'field'; action: 'repel'; steps: number } | { type: 'field'; action: 'escape' };
export type FieldAction = FieldEffect['action'];

export const FIELD_ACTIONS: Readonly<Record<FieldAction, { label: string; description: string }>> = {
  repel: { label: 'Repousse', description: 'Aucune rencontre sauvage pendant un nombre de pas' },
  escape: { label: 'Corde de sortie', description: 'Retour à la dernière borne de soin' },
};

export const isFieldAction = isKeyOf<FieldAction>(FIELD_ACTIONS);

/**
 * Ce que donne un équipement. `ice-skates` : on ne glisse plus sur la glace (zones à effets « glissade ») ;
 * `light` : une lumière autour du joueur, vue de tous (casque-lampe…).
 */
export type EquipmentEffect = { type: 'equipment'; ability: 'ice-skates' } | { type: 'equipment'; ability: 'light'; light: LightSource };
export type EquipmentAbility = EquipmentEffect['ability'];

export const EQUIPMENT_ABILITIES: Readonly<Record<EquipmentAbility, { label: string; description: string }>> = {
  'ice-skates': { label: 'Patins à glace', description: 'Ne glisse plus sur la glace' },
  light: { label: 'Lumière', description: 'Éclaire autour du joueur' },
};

export const isEquipmentAbility = isKeyOf<EquipmentAbility>(EQUIPMENT_ABILITIES);

export type ItemEffectType = ItemEffect['type'];

export const ITEM_EFFECT_LABELS: Readonly<Record<ItemEffectType, string>> = {
  none: 'Aucune (objet de quête)',
  heal: 'Soin',
  capture: 'Capture',
  equipment: 'Équipement',
  field: 'Sur la carte',
  move: 'Attaque (CT)',
};

/** Rubriques du sac à dos, dans l'ordre : les objets y sont rangés selon leur utilité. */
export const BAG_SECTIONS: readonly { effect: ItemEffectType; label: string }[] = [
  { effect: 'capture', label: 'Capture' },
  { effect: 'heal', label: 'Soin' },
  { effect: 'field', label: 'Sur la carte' },
  { effect: 'equipment', label: 'Équipement' },
  { effect: 'move', label: 'Attaques' },
  { effect: 'none', label: 'Objets de quête' },
];

/** Sous-rubrique d'un objet de quête dans le sac (« » : sans catégorie). */
export const questCategoryOf = (item: Pick<Item, 'effect'>): string => (item.effect.type === 'none' ? item.effect.category ?? '' : '');

/** Catégories d'objets de quête déjà utilisées, triées (pour en proposer une existante dans l'administration). */
export function questCategories(items: readonly Pick<Item, 'effect'>[]): string[] {
  return [...new Set(items.map(questCategoryOf).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/** Capacités données par les objets équipés (ceux qui ne sont pas des équipements ne comptent pas). */
export function abilitiesOf(equipped: Iterable<ItemId>, items: { get(id: ItemId): Item | undefined }): Set<EquipmentAbility> {
  const abilities = new Set<EquipmentAbility>();
  for (const id of equipped) {
    const effect = items.get(id)?.effect;
    if (effect?.type === 'equipment') abilities.add(effect.ability);
  }
  return abilities;
}

/** Lumière portée : celle du plus puissant des équipements lumineux équipés, ou null. */
export function equippedLight(equipped: Iterable<ItemId>, items: { get(id: ItemId): Item | undefined }): LightSource | null {
  return strongestLight([...equipped].map((id) => {
    const effect = items.get(id)?.effect;
    return effect?.type === 'equipment' && effect.ability === 'light' ? effect.light : null;
  }));
}

export interface ItemInput {
  name: string;
  description: string;
  /** Prix en boutique, en pièces (les objets en vente se choisissent carte par carte). */
  price: number;
  /** Icône et teinte affichées quand l'objet n'a pas d'image. */
  icon: IconName;
  color: string;
  effect: ItemEffect;
  /** Image PNG envoyée par l'administration (data URL) ; absente en modification : l'image est conservée. */
  image?: string;
  /** Vrai : l'image actuelle est retirée (l'icône reprend sa place). */
  removeImage?: boolean;
}

export interface Item extends Omit<ItemInput, 'image' | 'removeImage'> {
  id: ItemId;
  /** URL de l'image téléversée, ou null (l'icône est affichée). */
  image: string | null;
}

export const ITEM_LIMITS = { maxRepelSteps: 1000, name: 40, description: 2000, category: 30, maxPrice: 100_000, maxHeal: 9999, maxCaptureBonus: 10, maxImageBytes: 1024 * 1024, maxImageSide: 512 } as const;
export const ITEM_ID_PATTERN = /^[a-z0-9_-]{1,40}$/;

export type HealItem = Item & { effect: { type: 'heal'; amount: number } };
export type CaptureItem = Item & { effect: { type: 'capture'; bonus: number } };
export const isHealItem = (item: Item | undefined): item is HealItem => item?.effect.type === 'heal';
export const isCaptureItem = (item: Item | undefined): item is CaptureItem => item?.effect.type === 'capture';

/** Identifiant tiré du nom (« Super Potion » → « super-potion »), rendu unique par un suffixe si besoin. */
export function itemIdFromName(name: string, taken: ReadonlySet<string>): ItemId {
  const base = name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32) || 'objet';
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
  return id;
}

/** Objets créés avec la base (modifiables ensuite dans l'administration comme les autres). */
export const DEFAULT_ITEMS: readonly (Omit<Item, 'image'>)[] = [
  { id: 'capsule', name: 'Capsule', icon: 'item-capsule', color: '#ef4444', price: 20, effect: { type: 'capture', bonus: 1 }, description: 'Capsule standard pour capturer les {monstres} sauvages.' },
  { id: 'super_capsule', name: 'Super Capsule', icon: 'item-super-capsule', color: '#3b82f6', price: 60, effect: { type: 'capture', bonus: 1.6 }, description: 'Taux de capture amélioré.' },
  { id: 'hyper_capsule', name: 'Hyper Capsule', icon: 'item-hyper-capsule', color: '#eab308', price: 150, effect: { type: 'capture', bonus: 2.5 }, description: 'Idéale pour les {monstres} rares.' },
  { id: 'potion', name: 'Potion', icon: 'item-potion', color: '#a855f7', price: 30, effect: { type: 'heal', amount: 25 }, description: 'Rend 25 PV à {un monstre}.' },
  { id: 'super_potion', name: 'Super Potion', icon: 'item-super-potion', color: '#ec4899', price: 90, effect: { type: 'heal', amount: 70 }, description: 'Rend 70 PV à {un monstre}.' },
];

/** Sac de départ d'un nouveau dresseur (les objets supprimés depuis sont ignorés). */
export const STARTING_BAG: Readonly<Record<ItemId, number>> = { capsule: 10, potion: 2 };
export const STARTING_COINS = 150;

/** Ce que fait l'objet, en quelques mots (boutique, sac, aide). */
export function describeItemEffect(effect: ItemEffect): string {
  switch (effect.type) {
    case 'heal': return `+${effect.amount} PV`;
    case 'capture': return `Capture ×${effect.bonus}`;
    case 'none': return effect.category ? `Objet de quête · ${effect.category}` : 'Objet de quête';
    case 'field': return effect.action === 'repel' ? `Repousse · ${effect.steps} pas sans rencontre` : 'Retour à la dernière borne de soin';
    case 'move': return 'Attaque à apprendre · usage illimité';
    case 'equipment': return `Équipement · ${EQUIPMENT_ABILITIES[effect.ability].description}${effect.ability === 'light' ? ` (${effect.light.radius} cases)` : ''}`;
  }
}
